import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UsersThree, Plus, Trash, Clock, UserCircle } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePersonaId, setInvitePersonaId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, personasRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/personas/options`),
      ]);
      setUsers(usersRes.data);
      setPersonas(personasRes.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const inviteUser = async () => {
    if (!inviteEmail.trim() || !inviteEmail.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    if (!invitePersonaId) {
      toast.error('Select a persona');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/users/invite`, { email: inviteEmail.trim(), persona_id: invitePersonaId });
      toast.success(res.data.message || 'User invited');
      setInviteEmail('');
      setInvitePersonaId('');
      setShowInvite(false);
      fetchData();
    } catch (error) {
      console.error('Error inviting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to invite user');
    } finally {
      setSubmitting(false);
    }
  };

  const updateUserPersona = async (azureOid, personaId) => {
    try {
      await axios.put(`${API}/users/${azureOid}/persona`, { persona_id: personaId || null });
      toast.success('Persona updated');
      fetchData();
    } catch (error) {
      console.error('Error updating persona:', error);
      toast.error('Failed to update persona');
    }
  };

  const revokeUser = async (azureOid) => {
    if (!window.confirm('Revoke this user\'s access? They will lose access to all pages until reassigned.')) return;
    try {
      await axios.delete(`${API}/users/${azureOid}`);
      toast.success('Access revoked');
      fetchData();
    } catch (error) {
      console.error('Error revoking access:', error);
      toast.error('Failed to revoke access');
    }
  };

  const cancelInvite = async (email) => {
    if (!window.confirm('Cancel this pending invite?')) return;
    try {
      await axios.delete(`${API}/users/invites/${encodeURIComponent(email)}`);
      toast.success('Invite cancelled');
      fetchData();
    } catch (error) {
      console.error('Error cancelling invite:', error);
      toast.error('Failed to cancel invite');
    }
  };

  if (loading) {
    return (
      <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="h-96 bg-white border border-slate-200 rounded-lg skeleton" />
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12" data-testid="user-management-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-4xl sm:text-5xl font-medium tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              User Management
            </h1>
            <p className="text-base font-normal text-slate-600 leading-relaxed mt-2">
              Assign personas to users so they only see the pages they need
            </p>
          </div>
          <button
            onClick={() => setShowInvite(!showInvite)}
            className="flex items-center space-x-2 btn-primary px-5 py-2.5 rounded-md text-sm font-medium"
            data-testid="add-user-button"
          >
            <Plus size={18} weight="bold" />
            <span>Add User</span>
          </button>
        </div>

        {showInvite && (
          <div className="bg-white border border-slate-200 rounded-lg p-6 mb-8" data-testid="invite-user-form">
            <h3 className="text-lg font-medium text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Add User
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              Enter the user's work email and pick a persona. If they haven't signed in yet, the persona
              is applied automatically the first time they sign in with Azure AD.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="user@company.com"
                className="flex-1 px-4 py-2.5 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                data-testid="invite-email-input"
              />
              <select
                value={invitePersonaId}
                onChange={(e) => setInvitePersonaId(e.target.value)}
                className="px-4 py-2.5 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                data-testid="invite-persona-select"
              >
                <option value="">Select persona</option>
                {personas.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <button
                onClick={inviteUser}
                disabled={submitting}
                className="btn-primary px-5 py-2.5 rounded-md text-sm font-medium disabled:opacity-50"
                data-testid="submit-invite-button"
              >
                Add
              </button>
              <button
                onClick={() => setShowInvite(false)}
                className="px-5 py-2.5 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {users.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center" data-testid="empty-users-state">
            <div className="inline-flex bg-blue-50 p-4 rounded-lg mb-4">
              <UsersThree size={48} weight="duotone" className="text-[#002FA7]" />
            </div>
            <p className="text-lg font-medium text-slate-800 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              No users yet
            </p>
            <p className="text-base text-slate-600">
              Add a user by email and assign a persona to grant them page access.
            </p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="users-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-500">
                  <th className="px-6 py-3 font-medium">User</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Persona</th>
                  <th className="px-6 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const key = u.azure_oid || `invite-${u.email}`;
                  const isPending = u.status === 'pending';
                  return (
                    <tr key={key} className="border-b border-slate-100 last:border-0" data-testid={`user-row-${key}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <UserCircle size={28} weight="duotone" className="text-[#002FA7]" />
                          <div>
                            <p className="font-medium text-slate-900">{u.name || u.email}</p>
                            <p className="text-xs text-slate-500">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {isPending ? (
                          <span className="inline-flex items-center space-x-1 text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded">
                            <Clock size={12} />
                            <span>Pending sign-in</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-xs bg-green-50 text-green-700 px-2 py-1 rounded">
                            Active
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {isPending ? (
                          <span className="text-slate-700">{u.persona_name || '—'}</span>
                        ) : (
                          <select
                            value={u.persona_id || ''}
                            onChange={(e) => updateUserPersona(u.azure_oid, e.target.value)}
                            className="px-3 py-1.5 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                            data-testid={`persona-select-${u.azure_oid}`}
                          >
                            <option value="">No persona</option>
                            {personas.map((p) => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => (isPending ? cancelInvite(u.email) : revokeUser(u.azure_oid))}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title={isPending ? 'Cancel invite' : 'Revoke access'}
                          data-testid={`remove-user-${key}`}
                        >
                          <Trash size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
